<template>
  <v-container fluid>
    <!-- ============= SECTION 1 : Generation ============= -->
    <avs-card class="mb-4">
      <template v-slot:title>
        <div class="d-flex align-center" style="width: 100%; gap: 12px; flex-wrap: wrap;">
          <div>
            <div class="text-h6 font-weight-bold">
              <v-icon left color="primary">mdi-key-variant</v-icon>
              {{ $t("Cles API") }}
            </div>
            <div class="text-caption grey--text">
              {{ $t("Generez des cles pour utiliser l'API depuis vos systemes externes.") }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            text
            color="primary"
            :to="{ name: 'ApiDocs' }"
          >
            <v-icon left>mdi-book-open-variant</v-icon>
            {{ $t("Documentation") }}
          </v-btn>
        </div>
      </template>

      <template v-slot:content>
        <div class="generate-panel">
          <div class="generate-text">
            <div class="text-subtitle-1 font-weight-medium mb-1">
              {{ $t("Creer une nouvelle cle d'acces") }}
            </div>
            <div class="text-body-2 grey--text">
              {{ $t("La cle complete ne sera affichee qu'une seule fois apres la creation.") }}
              {{ $t("Ensuite, seuls les premiers caracteres seront visibles dans l'historique.") }}
            </div>
          </div>
          <v-btn color="primary" depressed large @click="openCreateDialog">
            <v-icon left>mdi-key-plus</v-icon>
            {{ $t("Generer une nouvelle cle") }}
          </v-btn>
        </div>
      </template>
    </avs-card>

    <!-- ============= SECTION 2 : Historique ============= -->
    <avs-card>
      <template v-slot:title>
        <div class="d-flex align-center" style="width: 100%; gap: 12px; flex-wrap: wrap;">
          <div>
            <div class="text-h6 font-weight-bold">
              <v-icon left>mdi-history</v-icon>
              {{ $t("Historique des cles generees") }}
            </div>
            <div class="text-caption grey--text">
              {{ items.length }} {{ items.length > 1 ? $t("cles") : $t("cle") }}
              ({{ activeCount }} {{ $t("actives") }},
              {{ revokedCount }} {{ $t("revoquees") }},
              {{ expiredCount }} {{ $t("expirees") }})
            </div>
          </div>
          <v-spacer />
          <v-btn icon small :title="$t('Rafraichir')" @click="fetchKeys">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:content>
        <v-data-table
          class="ma-4 bgTable"
          :headers="headers"
          :items="items"
          :loading="loading"
          :no-data-text="$t('Aucune cle API generee pour le moment')"
          :loading-text="$t('Chargement...')"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          item-key="id"
        >
          <template v-slot:item.name="{ item }">
            <div class="font-weight-medium">{{ item.name }}</div>
            <div class="text-caption grey--text">
              {{ $t("par") }} {{ item.created_by_name || "—" }}
            </div>
          </template>

          <template v-slot:item.partial_key="{ item }">
            <code class="prefix-chip" :title="$t('Cle partielle (les caracteres masques ne sont pas recuperables)')">
              gwa_<strong>{{ item.prefix }}</strong>_••••••••••••
            </code>
          </template>

          <template v-slot:item.validity="{ item }">
            <div class="text-body-2">
              <v-icon x-small color="grey">mdi-calendar-start</v-icon>
              {{ formatDate(item.created_at) }}
            </div>
            <div class="text-caption">
              <v-icon x-small color="grey">mdi-calendar-end</v-icon>
              <span v-if="item.expires_at">
                {{ $t("Expire le") }} {{ formatDate(item.expires_at) }}
              </span>
              <span v-else class="grey--text">
                {{ $t("Sans expiration") }}
              </span>
            </div>
          </template>

          <template v-slot:item.last_used_at="{ item }">
            <span v-if="item.last_used_at">
              {{ formatDate(item.last_used_at) }}
              <span v-if="item.last_used_ip" class="text-caption grey--text d-block">
                {{ item.last_used_ip }}
              </span>
            </span>
            <span v-else class="grey--text">{{ $t("Jamais") }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="statusColor(item.status)"
              text-color="white"
            >
              {{ statusLabel(item.status) }}
            </v-chip>
            <div v-if="item.status === 'revoked' && item.revoked_at" class="text-caption grey--text mt-1">
              {{ $t("le") }} {{ formatDate(item.revoked_at) }}
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="item.status === 'active'"
              icon
              small
              color="warning"
              :title="$t('Revoquer')"
              @click="askRevoke(item)"
            >
              <v-icon small>mdi-cancel</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="error"
              :title="$t('Supprimer')"
              @click="askDelete(item)"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </template>
    </avs-card>

    <!-- Dialog: creation -->
    <v-dialog v-model="createDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">
          {{ $t("Nouvelle cle API") }}
        </v-card-title>
        <v-card-text>
          <v-form ref="createForm" v-model="createValid">
            <v-text-field
              v-model="form.name"
              :label="$t('Nom')"
              :hint="$t('Ex: ERP interne, integration comptable...')"
              :rules="[v => !!v || $t('Le nom est requis')]"
              outlined
              dense
              required
            />
            <v-menu
              v-model="expiresMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.expires_at"
                  :label="$t('Expiration (optionnel)')"
                  :hint="$t('Laissez vide pour une cle sans expiration')"
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  dense
                  readonly
                  clearable
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="form.expires_at"
                :min="todayStr"
                @input="expiresMenu = false"
              />
            </v-menu>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCreateDialog">{{ $t("Annuler") }}</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="creating"
            :disabled="!createValid"
            @click="submitCreate"
          >
            {{ $t("Generer") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: cle generee (affichee 1 seule fois) -->
    <v-dialog v-model="generatedDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="text-h6 warning--text">
          <v-icon left color="warning">mdi-alert</v-icon>
          {{ $t("Cle API generee") }}
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" text dense>
            {{ $t("Cette cle ne sera plus affichee apres fermeture. Copiez-la maintenant.") }}
          </v-alert>
          <div class="generated-key-box">
            <code>{{ generatedKey }}</code>
          </div>
          <v-btn
            block
            color="primary"
            depressed
            class="mt-3"
            @click="copyKey"
          >
            <v-icon left>mdi-content-copy</v-icon>
            {{ copied ? $t("Copie !") : $t("Copier la cle") }}
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" text @click="closeGeneratedDialog">
            {{ $t("J'ai copie la cle") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: confirmation revocation -->
    <v-dialog v-model="confirmRevoke" max-width="450">
      <v-card>
        <v-card-title class="text-h6">{{ $t("Revoquer cette cle ?") }}</v-card-title>
        <v-card-text>
          {{ $t("La cle") }} <strong>{{ targetItem && targetItem.name }}</strong>
          {{ $t("ne pourra plus etre utilisee. Cette action est definitive.") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmRevoke = false">{{ $t("Annuler") }}</v-btn>
          <v-btn color="warning" depressed :loading="acting" @click="doRevoke">
            {{ $t("Revoquer") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: confirmation suppression -->
    <v-dialog v-model="confirmDelete" max-width="450">
      <v-card>
        <v-card-title class="text-h6">{{ $t("Supprimer cette cle ?") }}</v-card-title>
        <v-card-text>
          {{ $t("La cle") }} <strong>{{ targetItem && targetItem.name }}</strong>
          {{ $t("sera definitivement supprimee.") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDelete = false">{{ $t("Annuler") }}</v-btn>
          <v-btn color="error" depressed :loading="acting" @click="doDelete">
            {{ $t("Supprimer") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ApiKeyService } from "@/services"
import { toDatetime } from "@/utils/datetime"

export default {
  name: "ApiKeys",

  data() {
    const today = new Date().toISOString().slice(0, 10)
    return {
      loading: false,
      items: [],

      // create
      createDialog: false,
      createValid: false,
      creating: false,
      form: { name: "", expires_at: null },
      expiresMenu: false,
      todayStr: today,

      // generated
      generatedDialog: false,
      generatedKey: "",
      copied: false,

      // actions
      confirmRevoke: false,
      confirmDelete: false,
      targetItem: null,
      acting: false,

      sortBy: "created_at",
      sortDesc: true,

      headers: [
        { text: this.$t("Nom"), value: "name" },
        { text: this.$t("Cle (partielle)"), value: "partial_key", sortable: false },
        { text: this.$t("Validite"), value: "validity", sortable: false },
        { text: this.$t("Derniere utilisation"), value: "last_used_at" },
        { text: this.$t("Statut"), value: "status", align: "center" },
        { text: this.$t("Actions"), value: "actions", sortable: false, align: "center" },
      ],
    }
  },

  computed: {
    activeCount() {
      return this.items.filter(k => k.status === "active").length
    },
    revokedCount() {
      return this.items.filter(k => k.status === "revoked").length
    },
    expiredCount() {
      return this.items.filter(k => k.status === "expired").length
    },
  },

  created() {
    this.fetchKeys()
  },

  methods: {
    formatDate(v) {
      if (!v) return ""
      try {
        return toDatetime(v)
      } catch (e) {
        return new Date(v).toLocaleString("fr-FR")
      }
    },

    statusColor(s) {
      if (s === "active") return "success"
      if (s === "expired") return "grey"
      return "error"
    },

    statusLabel(s) {
      if (s === "active") return this.$t("Active")
      if (s === "expired") return this.$t("Expiree")
      return this.$t("Revoquee")
    },

    fetchKeys() {
      this.loading = true
      ApiKeyService.list()
        .then(r => {
          this.items = Array.isArray(r) ? r : (r?.results || [])
        })
        .catch(() => {
          this.items = []
          this.$store.commit("app/message", {
            msg: this.$t("Impossible de charger les cles API"),
            color: "error",
          })
        })
        .finally(() => { this.loading = false })
    },

    openCreateDialog() {
      this.form = { name: "", expires_at: null }
      this.createDialog = true
      this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.resetValidation())
    },

    closeCreateDialog() {
      this.createDialog = false
    },

    submitCreate() {
      if (!this.$refs.createForm.validate()) return
      this.creating = true
      const payload = { name: this.form.name }
      if (this.form.expires_at) {
        const d = new Date(this.form.expires_at + "T23:59:59")
        payload.expires_at = d.toISOString()
      }
      ApiKeyService.create(payload)
        .then(r => {
          this.generatedKey = r.key
          this.generatedDialog = true
          this.createDialog = false
          this.fetchKeys()
        })
        .catch(err => {
          const msg = err?.response?.data?.detail
            || err?.response?.data?.name?.[0]
            || this.$t("Echec de la creation de la cle")
          this.$store.commit("app/message", { msg, color: "error" })
        })
        .finally(() => { this.creating = false })
    },

    copyKey() {
      navigator.clipboard.writeText(this.generatedKey)
        .then(() => {
          this.copied = true
          setTimeout(() => { this.copied = false }, 2000)
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: this.$t("Impossible de copier la cle"),
            color: "error",
          })
        })
    },

    closeGeneratedDialog() {
      this.generatedDialog = false
      this.generatedKey = ""
    },

    askRevoke(item) {
      this.targetItem = item
      this.confirmRevoke = true
    },

    doRevoke() {
      this.acting = true
      ApiKeyService.revoke(this.targetItem.id)
        .then(() => {
          this.confirmRevoke = false
          this.fetchKeys()
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: this.$t("Echec de la revocation"),
            color: "error",
          })
        })
        .finally(() => { this.acting = false })
    },

    askDelete(item) {
      this.targetItem = item
      this.confirmDelete = true
    },

    doDelete() {
      this.acting = true
      ApiKeyService.remove(this.targetItem.id)
        .then(() => {
          this.confirmDelete = false
          this.fetchKeys()
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: this.$t("Echec de la suppression"),
            color: "error",
          })
        })
        .finally(() => { this.acting = false })
    },
  },
}
</script>

<style scoped>
.prefix-chip {
  background: #f5f5f5;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-family: "Courier New", monospace;
  color: #424242;
  letter-spacing: 0.3px;
}
.prefix-chip strong {
  color: #1976d2;
  font-weight: 700;
}

.generate-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border: 1px dashed rgba(25, 118, 210, 0.3);
  border-radius: 8px;
  flex-wrap: wrap;
}

.generate-text {
  flex: 1;
  min-width: 240px;
}

.generated-key-box {
  background: #1e1e1e;
  color: #ffd700;
  padding: 16px;
  border-radius: 6px;
  font-family: monospace;
  font-size: 14px;
  word-break: break-all;
  margin-top: 12px;
}
</style>
